import Plan from '@/classes/Plan'
import type { PriceBook } from '@/classes/PriceBook'
import { DocumentType } from '@/enums/DocumentType'
import { PlanEverplansPaymentType } from '@/enums/PlanEverplansPaymentType'
import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import { useRetryMethods } from '@/services/retryUtils'
import type { SubmissionService } from '@/services/types'
import { useAuthStore } from '@/stores/auth'
import { useCrmStore } from '@/stores/crm'
import { useDocumentStore } from '@/stores/document'
import { useSessionStore } from '@/stores/session'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import type {
	CreditCardEverplansPayment,
	EftEverplansPayment,
	EverplansSubmitRequest,
	MergedDocument,
	StoreDocument,
} from '@/types'
import { captureException } from '@sentry/vue'
import { type AxiosRequestConfig } from 'axios'
import localforageLib from 'localforage'
import { DateTime } from 'luxon'

const localEverplansSubmissions = localforageLib.createInstance({
	name: 'request-queue',
	storeName: 'everplansSubmissions',
})

const everplansSubmissionsQueueConfig = {
	axiosInstance: purchaseApiAxiosInstance,
	requestBackgroundSync: async (tag: string) => {
		await windowRequestQueueConfig.requestBackgroundSync(tag)
	},
}

const everplansSubmissionRequestQueue = useRequestQueue(
	'everplansSubmissions',
	everplansSubmissionsQueueConfig
)

// This getter exposes the everplans request queue to submission store
export const getQueue = () => {
	return everplansSubmissionRequestQueue
}

// This method checks whether Everplans should submit
// if so, it creates the Everplans submission request and adding it to the queue
export const submitRequest = async (plan: Plan) => {
	if (!shouldSubmit(plan)) {
		return
	}

	const documentStore = useDocumentStore()
	const mergedDocument = documentStore.getPlanHolderDocumentByType(
		plan,
		DocumentType.EverplansMergedDocument
	)
	const goodsServicesDocument = documentStore.getPlanHolderDocumentByType(
		plan,
		DocumentType.StatementOfGoodsAndServices
	)

	const sessionStore = useSessionStore()
	const priceBook = sessionStore.activePriceBook
	let reportFhId = sessionStore.activeFh?.reportFhId

	if (!reportFhId) {
		reportFhId = getFuneralHomeId(plan)
	}

	const data = mapEverplansSubmission(
		plan,
		mergedDocument,
		goodsServicesDocument,
		reportFhId,
		priceBook
	)

	const everplansRequestConfig: AxiosRequestConfig = {
		url: `/v1/everplans/submit`,
		method: 'POST',
		withCredentials: true,
		headers: {
			'Content-Type': 'multipart/form-data',
		},
		data,
	}

	try {
		await everplansSubmissionRequestQueue.addRequest(
			plan.id,
			everplansRequestConfig
		)
	} catch (error) {
		// only log to sentry, executeRequest takes care of updating appropriate status in the localForage
		captureException(error, {
			extra: {
				message: 'Error submitting data to Everplans.',
			},
		})
	}
}

// Use the crm store to get the report funeral home id if it exists
const getFuneralHomeId = (planHolder: Plan): string => {
	const crmStore = useCrmStore()
	const planFuneralHome = crmStore.findFuneralHome(planHolder.funeralHomeId)

	return planFuneralHome?.reportFhId ?? planHolder.funeralHomeId
}

// Helper to ensure EFT paymentDetails has all appropriate keys and the values are of right type
const validateEverplansEFTPaymentDetails = (
	paymentDetails: EftEverplansPayment['paymentDetails']
) => {
	const eftPaymentKeys: (keyof EftEverplansPayment['paymentDetails'])[] = [
		'bankName',
		'accountHolder',
		'accountNumber',
		'accountType',
		'routingNumber',
	]

	let isValid = true

	for (const key of eftPaymentKeys) {
		if (
			!Object.hasOwn(paymentDetails, key) ||
			typeof paymentDetails[key] !== 'string' ||
			!paymentDetails[key]
		) {
			isValid = false
			break
		}
	}

	return isValid
}

// Helper to ensure Stripe paymentDetails has all appropriate keys and the values are of right type
const validateEverplansStripePaymentDetails = (
	paymentDetails: CreditCardEverplansPayment['paymentDetails']
) => {
	return Object.hasOwn(paymentDetails, 'token') && paymentDetails.token !== ''
}

const getNoCostEverplansPackage = (
	plan: Plan,
	priceBook?: PriceBook
): { id: string; name: string } | null => {
	for (const cartItem of plan.cartItems) {
		const lineItem = priceBook?.findLineItem(cartItem.lineItemId)

		if (lineItem?.isNoCostEverplans) {
			return {
				id: lineItem.id,
				name: lineItem.name,
			}
		}
	}

	return null
}

const mapEverplansSubmission = (
	plan: Plan,
	mergedDocument?: MergedDocument,
	goodsServicesDocument?: StoreDocument,
	reportFhId?: string,
	priceBook?: PriceBook
): EverplansSubmitRequest => {
	const completedPdf = mergedDocument
		? new File(
				[mergedDocument.blob],
				`${plan.firstName}-${plan.lastName}-${DateTime.now().toFormat('MM-dd-yyyy')}`,
				{ type: 'application/pdf' }
			)
		: null
	const goodsServicesPdf = goodsServicesDocument
		? new File(
				[goodsServicesDocument.blob],
				`${plan.firstName}-${plan.lastName}-goods-and-services-${DateTime.now().toFormat('MM-dd-yyyy')}`,
				{ type: 'application/pdf' }
			)
		: null

	const mappedCartItems = plan?.cartItems
		.map((cartItem) => {
			const pbCartItem = priceBook?.findLineItem(cartItem.lineItemId)
			if (pbCartItem?.isVirtualLine) {
				return null
			}

			const subItems = cartItem.subItems
				.map((subItem) => {
					const pbSubItem = priceBook?.findLineItem(subItem.lineItemId)
					if (pbSubItem?.isVirtualLine) {
						return null
					}
					return {
						...subItem,
						lineItem: pbSubItem,
					}
				})
				.filter((subCartItem) => !!subCartItem)

			return {
				...cartItem,
				lineItem: pbCartItem,
				subItems,
			}
		})
		.filter((cartItem) => !!cartItem)

	// Looking up FH name again as Everplans may not have a funeralHome registered on their side, they need a funeral home name to send out invites (Refer to PSS-2004)
	const planFuneralHome = useCrmStore().findFuneralHome(plan.funeralHomeId)

	// clear errors from plan data
	const authStore = useAuthStore()
	const salesperson = authStore?.user?.salesContact

	const salesforceContactAfpId = authStore.getActiveSalesContactId()
	const planData = JSON.stringify({
		plan: {
			...{ ...plan, errors: [] },
			cartItems: mappedCartItems,
			funeralHomeName:
				planFuneralHome?.name ?? planFuneralHome?.shortName ?? '',
		},
		afp: {
			id: salesforceContactAfpId,
			firstName: salesperson?.firstname,
			lastName: salesperson?.lastname,
			email: salesperson?.email,
			phone: salesperson?.mobilePhone,
		},
	})

	const sessionDocumentStore = useSessionDocumentsStore()
	const isEverplansCompleted = sessionDocumentStore.isEverplansCompleted(
		plan.id
	)

	let everplansIncluded = plan.everplansIncluded
	let everplansAmount = plan.everplansPayment.amount

	const noCostEverplansPackage = getNoCostEverplansPackage(plan, priceBook)

	// always downgrade plan to a freemium without everplans document completed
	if (everplansIncluded && !isEverplansCompleted) {
		everplansIncluded = false
		everplansAmount = 0
	}

	let paymentDetails = {}

	const paymentType = plan.everplansPayment.paymentType

	if (everplansIncluded) {
		switch (paymentType) {
			case PlanEverplansPaymentType.CreditCard: {
				paymentDetails = {
					stripeToken: plan.everplansPayment.paymentDetails.token,
				}
				break
			}
			case PlanEverplansPaymentType.EFT: {
				const eftPaymentDetails = plan.everplansPayment.paymentDetails

				paymentDetails = {
					eftPayment: {
						bankName: eftPaymentDetails.bankName,
						accountHolder: eftPaymentDetails.accountHolder,
						accountNumber: eftPaymentDetails.accountNumber,
						accountType: eftPaymentDetails.accountType,
						routingNumber: eftPaymentDetails.routingNumber,
					},
				}
				break
			}
		}
	}

	// if everplans is not included
	// make sure to set everplansAmount to 0 and paymentDetails to empty object
	if (!everplansIncluded) {
		everplansAmount = 0
		paymentDetails = {}
	}

	// If no-cost everplans package is found and everplans is included
	// make sure to set everplansIncluded to true, everplansAmount to 0
	// and paymentDetails to empty object, to signify this was a no-cost everplans package
	if (noCostEverplansPackage && plan.everplansIncluded) {
		everplansIncluded = true
		everplansAmount = 0
		paymentDetails = {}
	}

	return {
		planId: plan.id,
		salesforceContactAfpId: salesforceContactAfpId ?? null,
		salesforceReportFhId: reportFhId ?? null,
		everplansPurchased: everplansIncluded ? 1 : 0,
		everplansAmount: everplansAmount,
		isTestSubmission: isSubmissionForTestFh(plan.funeralHomeId),
		...paymentDetails,
		package:
			noCostEverplansPackage && everplansIncluded
				? {
						id: noCostEverplansPackage.id,
						name: noCostEverplansPackage.name,
					}
				: null,
		planData,
		completedPdf,
		goodsServicesPdf,
	}
}
/**
 * Determines if the Funeral Home is a test funeral home, indicating a test submission
 * See PSS-1683
 * @param funeralHomeId
 */
const isSubmissionForTestFh = (funeralHomeId: string | undefined): 0 | 1 => {
	return funeralHomeId === import.meta.env.VITE_INTERNAL_FUNERAL_HOME_ID ? 1 : 0
}

export const validateRequest = (planHolder: Plan): boolean => {
	const everPlansPayment = planHolder.everplansPayment

	if (!planHolder.everplansIncluded) {
		return false
	}

	const priceBook = useSessionStore().activePriceBook

	if (getNoCostEverplansPackage(planHolder, priceBook)) {
		return true
	}

	if (everPlansPayment.paymentType === PlanEverplansPaymentType.CreditCard) {
		return validateEverplansStripePaymentDetails(
			everPlansPayment.paymentDetails
		)
	} else if (everPlansPayment.paymentType === PlanEverplansPaymentType.EFT) {
		return validateEverplansEFTPaymentDetails(everPlansPayment.paymentDetails)
	}

	return false
}

const shouldSubmit = (plan: Plan): boolean => {
	// don't register people without emails for everplans
	// or if the sales contact is not authorized to sell Everplans

	return useAuthStore().isEverplansAuthorized
}

export const everplansService: SubmissionService = {
	getQueue,
	submitRequest,
	validateRequest,
	serviceName: 'Everplans',
	storeName: 'everplansSubmissions',
	shouldSubmit,
	...useRetryMethods(
		everplansSubmissionRequestQueue,
		localEverplansSubmissions,
		'Everplans'
	),
}
